// Store de abas internas do admin (Zustand), SESSION-ONLY: não persiste em localStorage nem servidor
// (limpo no logout). Segue a convenção do projeto: Zustand só para sessão/UI. As ações retornam
// "intenções" de navegação que a UI (TabBar/host) executa, mantendo a lógica desacoplada do router.
import { create } from "zustand";
import {
  closeTab as closeTabModel,
  syncTabs,
  tabKeyOf,
  type AdminTab,
  type SyncResult,
  type TabLocation,
} from "@/layouts/adminTabs/tabModel";
import { titleForAdminLocation } from "@/layouts/adminTabs/adminTabTitle";

type AdminTabsState = {
  tabs: AdminTab[];
  activeKey: string | null;
  dirty: Record<string, boolean>;

  sync: (loc: TabLocation) => SyncResult;
  goHome: () => void;
  performClose: (key: string) => { nextLocation: TabLocation | null; wentHome: boolean };
  isDirty: (key: string) => boolean;
  setDirty: (key: string, dirty: boolean) => void;
  setTitle: (key: string, title: string) => void;
  clearAll: () => void;
};

export const useAdminTabs = create<AdminTabsState>((set, get) => ({
  tabs: [],
  activeKey: null,
  dirty: {},

  sync: (loc) => {
    const { tabs, activeKey } = get();
    const result = syncTabs(tabs, loc, titleForAdminLocation, activeKey);
    if (result.result.type !== "blocked") {
      set({ tabs: result.tabs, activeKey: result.activeKey });
    }
    return result.result;
  },

  goHome: () => {
    if (get().activeKey !== null) set({ activeKey: null });
  },

  performClose: (key) => {
    const { tabs, activeKey, dirty } = get();
    const closed = closeTabModel(tabs, activeKey, key);
    const nextDirty = { ...dirty };
    delete nextDirty[key];
    set({ tabs: closed.tabs, activeKey: closed.activeKey, dirty: nextDirty });
    return { nextLocation: closed.nextLocation, wentHome: closed.wentHome };
  },

  isDirty: (key) => Boolean(get().dirty[tabKeyOf(key)]),

  setDirty: (key, dirty) =>
    set((state) => {
      const normalized = tabKeyOf(key);
      if (Boolean(state.dirty[normalized]) === dirty) return state;
      const next = { ...state.dirty };
      if (dirty) next[normalized] = true;
      else delete next[normalized];
      return { dirty: next };
    }),

  // Permite que uma tela de detalhe substitua o título genérico da sua aba pelo nome da entidade
  // carregada (ex.: nome do estabelecimento no lugar do UUID da rota). Só atualiza se a aba existir e
  // o título realmente mudar — evita re-render desnecessário.
  setTitle: (key, title) =>
    set((state) => {
      const normalized = tabKeyOf(key);
      const idx = state.tabs.findIndex((t) => t.key === normalized);
      if (idx < 0 || !title || state.tabs[idx].title === title) return state;
      const tabs = state.tabs.map((t, i) => (i === idx ? { ...t, title } : t));
      return { tabs };
    }),

  clearAll: () => set({ tabs: [], activeKey: null, dirty: {} }),
}));
